// What Vio may say about a visitor's jurisdiction.
//
// The global product serves many countries and holds real depth in a few. That
// asymmetry IS the honest story — the backend's own resolver returns "unknown"
// as a real answer and analyses such contracts as general commercial
// agreements rather than borrowing another country's law. Vio should say the
// same thing, per country, and never more.
//
// Three tiers, taken from the live catalogue rather than a list kept here:
//
//   adapters  — `verified` is reachable: the claim's provision text is
//               retrieved from an official source and its terms checked.
//   depth     — the corpus holds jurisdiction-specific law, but there is no
//               adapter, so citations resolve to `unverified`. That is an
//               honest answer, not a failure.
//   supported — we can bill there. No local-law depth at all.
//
// A country in none of the three is not a gap to paper over: it is the general
// commercial-contract path, which the product does deliberately and says so.

export type Coverage = {
  adapters: string[];
  depth: string[];
  supported: string[];
};

export const EMPTY_COVERAGE: Coverage = { adapters: [], depth: [], supported: [] };

/** ISO-3166 alpha-2 guessed from the browser, or null. */
export function guessCountry(): string | null {
  if (typeof navigator === "undefined") return null;
  const tags = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean);
  for (const tag of tags) {
    try {
      const region = new Intl.Locale(tag).region;
      if (region && /^[A-Z]{2}$/.test(region)) return region;
    } catch {
      // Malformed tag from an exotic browser — try the next one.
    }
  }
  return null;
}

/**
 * The country's name, from the browser rather than a list in this repo.
 *
 * Rendered in ENGLISH, not the viewer's locale. `useCountryCatalogue` uses the
 * viewer's locale, and is right to — it labels options in a picker, where the
 * reader's own language is the friendly choice. Here the name is embedded in
 * an English sentence on an English-only site, and "Looks like Deutschland —
 * I check citations there" reads as a bug rather than as a courtesy.
 */
export function countryName(code: string): string {
  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Does this country have an adapter? The catalogue lists sub-national codes
 * (`US-CA`, `CA-ON`) alongside country codes, and a browser only ever gives us
 * a country — so `US` counts as adapter-backed on the strength of California.
 * That is deliberately narrow in what it then CLAIMS: the line says citations
 * can be checked "for some US jurisdictions", never that all of them can.
 */
function tierFor(country: string, cov: Coverage) {
  const hasAdapter = cov.adapters.some(
    (code) => code === country || code.startsWith(`${country}-`),
  );
  if (hasAdapter) return "adapter" as const;
  if (cov.depth.includes(country)) return "depth" as const;
  if (cov.supported.includes(country)) return "supported" as const;
  return "none" as const;
}

/** True when the adapter evidence is sub-national only (US, CA). */
function subNationalOnly(country: string, cov: Coverage): boolean {
  return (
    !cov.adapters.includes(country) &&
    cov.adapters.some((code) => code.startsWith(`${country}-`))
  );
}

/**
 * One line about this country, or null when we know nothing worth saying.
 *
 * Phrased with the country as a NOUN — "local law for France", never "France
 * law" and never "French law". Country names are not adjectives, and the
 * adjectival forms are irregular enough that getting them right would mean
 * keeping a demonym list: a second coverage list to drift, which is exactly
 * what reading the catalogue avoids.
 * Every branch is a statement the backend can back.
 */
export function coverageLine(country: string | null, cov: Coverage): string | null {
  if (!country) return null;
  const name = countryName(country);
  switch (tierFor(country, cov)) {
    case "adapter":
      return subNationalOnly(country, cov)
        ? `Looks like ${name} — for some ${name} jurisdictions I check citations against the official legislation source.`
        : `Looks like ${name} — I check citations there against the official legislation source.`;
    case "depth":
      return `Looks like ${name} — I hold local law for ${name}. Citations there I can locate, but not yet confirm word for word.`;
    case "supported":
      return `Looks like ${name} — I don’t hold local law there, so I’d read your contract as a general commercial agreement and say so.`;
    default:
      return null;
  }
}
